import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCompany, refreshCompany } from "@/lib/useCompany";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Upload, ExternalLink } from "lucide-react";
import RecoveryCenter from "@/components/RecoveryCenter";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { company, reload } = useCompany();
  const [profile, setProfile] = useState({ name: "", tagline: "", logo_url: "", address: "", phone: "", email: "", website: "", gstin: "" });
  const [saving, setSaving] = useState(false);
  const canEditBrand = ["super_admin", "director"].includes(user?.role);

  useEffect(() => { if (company) setProfile((p) => ({ ...p, ...company })); }, [company]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company", profile);
      await refreshCompany();
      await reload();
      toast.success("Company profile updated · branding will refresh across the app");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Studio</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-2">Update your company profile — the logo, name, and contact details automatically flow into every PDF, Excel export, email, and printed document.</p>
      </div>

      <section className="rounded-2xl p-6 bg-card border border-border/60 space-y-5" data-testid="settings-company">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Company Profile</div>
            <div className="text-xs text-muted-foreground mt-1">Central branding — one change ripples everywhere.</div>
          </div>
          {canEditBrand && (
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-company-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          )}
        </div>

        <div className="flex items-start gap-4 p-4 rounded-xl border border-border/50 bg-muted/20">
          <img src={profile.logo_url || company.logo_url} alt="logo" className="h-16 w-16 rounded-lg bg-white p-2 object-contain ring-1 ring-black/10" data-testid="company-logo-preview" />
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Logo URL</Label>
            <Input value={profile.logo_url} onChange={(e) => setProfile({ ...profile, logo_url: e.target.value })} placeholder="https://…/logo.png" disabled={!canEditBrand} data-testid="company-logo-input" />
            <div className="text-[11px] text-muted-foreground mt-1">Paste a public image URL. Aspect ratio is preserved. Recommended 512×512 or square SVG/PNG.</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><Label>Company name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} disabled={!canEditBrand} data-testid="company-name-input" /></div>
          <div><Label>Tagline</Label><Input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} disabled={!canEditBrand} /></div>
          <div className="col-span-2"><Label>Address</Label><Input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} disabled={!canEditBrand} data-testid="company-address-input" /></div>
          <div><Label>Phone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} disabled={!canEditBrand} data-testid="company-phone-input" /></div>
          <div><Label>Email</Label><Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} disabled={!canEditBrand} data-testid="company-email-input" /></div>
          <div><Label>Website</Label><Input value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })} disabled={!canEditBrand} /></div>
          <div><Label>GSTIN</Label><Input value={profile.gstin} onChange={(e) => setProfile({ ...profile, gstin: e.target.value })} disabled={!canEditBrand} /></div>
        </div>
      </section>

      <section className="rounded-2xl p-6 bg-card border border-border/60 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Account</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Signed in as</Label><Input value={user?.name || ""} readOnly /></div>
          <div><Label>Email</Label><Input value={user?.email || ""} readOnly /></div>
          <div><Label>Role</Label><Input value={user?.role || ""} readOnly className="capitalize" /></div>
        </div>
      </section>

      <section className="rounded-2xl p-6 bg-card border border-border/60 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Appearance</div>
        <div className="flex items-center justify-between">
          <div><div className="text-sm font-medium">Dark mode</div><div className="text-xs text-muted-foreground">Toggle dark charcoal / warm sand theme</div></div>
          <Switch checked={theme === "dark"} onCheckedChange={toggle} />
        </div>
      </section>

      <section className="rounded-2xl p-6 bg-card border border-border/60 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">User & Access Management</div>
        <p className="text-sm text-muted-foreground">Add employees, assign roles, and grant per-module permissions from the dedicated User Management screen.</p>
        <a href="/users" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Open User Management</a>
      </section>

      <RecoveryCenter user={user} />
    </div>
  );
}
